import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, Search, Bell, Plus, X } from 'lucide-react'
import { useUiStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { patientApi } from '@/api/patientApi'
import { cn, formatMobile, getInitials } from '@/lib/utils'

export function Navbar() {
  const { toggleSidebar } = useUiStore()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const searchRef = useRef(null)
  const debounce = useRef()

  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }
    clearTimeout(debounce.current)
    debounce.current = setTimeout(async () => {
      try {
        const data = await patientApi.list({ search: query, limit: 6 })
        setResults(Array.isArray(data) ? data : [])
        setOpen(true)
      } catch {
        /* error toast handled by axios interceptor */
      }
    }, 250)
    return () => clearTimeout(debounce.current)
  }, [query])

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [])

  const goToPatient = (id) => {
    setOpen(false)
    setQuery('')
    navigate(`/patients/${id}`)
  }

  return (
    <header className="sticky top-0 z-20 border-b border-ink-100 bg-cream/85 backdrop-blur-md">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
        <button
          onClick={toggleSidebar}
          className="rounded-lg p-2 text-ink-700 hover:bg-ink-100 lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Search */}
        <div ref={searchRef} className="relative flex-1 max-w-xl">
          <div
            className={cn(
              'flex items-center gap-2 rounded-xl bg-white px-3.5 transition-all duration-150',
              'ring-1 ring-inset ring-ink-200 hover:ring-ink-300',
              'focus-within:ring-2 focus-within:ring-primary-500',
              'h-10',
            )}
          >
            <Search className="h-4 w-4 shrink-0 text-ink-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => results.length && setOpen(true)}
              placeholder="Search patients by name, mobile, or ID…"
              className="h-full w-full bg-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('')
                  setResults([])
                  setOpen(false)
                }}
                className="rounded-md p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Results dropdown */}
          {open && (
            <div className="absolute left-0 right-0 mt-2 overflow-hidden rounded-2xl bg-white shadow-lift ring-1 ring-ink-100 animate-slide-up">
              {results.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-ink-500">
                  No patients match “{query}”
                </div>
              ) : (
                <ul className="max-h-80 overflow-y-auto py-2">
                  {results.map((p) => (
                    <li key={p.patient_id}>
                      <button
                        onClick={() => goToPatient(p.patient_id)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-primary-50"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-xs font-bold text-primary-800">
                          {getInitials(p.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-ink-900">
                            {p.name}
                          </p>
                          <p className="truncate text-xs text-ink-500">
                            {p.patient_id} · {formatMobile(p.mobile_number)}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            className="hidden h-10 w-10 items-center justify-center rounded-xl text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-white hover:ring-ink-300 sm:flex"
            title="Notifications"
          >
            <Bell className="h-4 w-4" />
          </button>
          <Button
            onClick={() => navigate('/patients/new')}
            leftIcon={<Plus className="h-4 w-4" />}
            size="md"
          >
            <span className="hidden sm:inline">New Patient</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>
    </header>
  )
}
